/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	createContext,
	Dispatch,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { io, Socket } from "socket.io-client";

import { updatePermissions } from "@/pages/Public/redux/auth.slice";
import { useAppDispatch } from "@/redux/hooks";

import { PermissionRoles } from "@/enum";

import { User } from "@/pages/Public/pages/Login/schema/login";

import { AuthState } from "@/types/auth";

import {
	IStageContentInfo,
	SelectedFileAndPage,
	UploadedTempFile,
	UploadedTempFilePage,
} from "../../Courses/constants/emptyCourseScenario";
import { UserSchemaType } from "../../Users/schema/invite-user";
import { ScenarioActionTypes } from "../enums/ScenarioActionTypes.enum";
import { ScenarioMessageTypes } from "../enums/ScenarioMessageTypes.enum";
import { IConnectedUser } from "../interfaces/IConnectedUser.interface";
import { ScenarioMessageInfo } from "../interfaces/ScenarioMessageInfo.interface";
import { ScenarioProgress } from "../interfaces/ScenarioProgress.interface";
import { IRaisedHands } from "../interfaces/IRaisedHands.interface";

interface SocketContextType {
	socket: Socket | null;
	currentStage: number;
	isCurrentUserERR: boolean;
	errInfo?: UserSchemaType;
	uploadedFiles: UploadedTempFile[];
	currentUserData: User | null;
	connectedUsers: IConnectedUser[];
	/** Everyone in the run, removed people included, so older entries still find their names. */
	runUsers: IConnectedUser[];
	chatMessages: ScenarioMessageInfo[];
	protocolMessages: ScenarioMessageInfo[];
	raisedHands: IRaisedHands[];
	currentStageEndTimestamp: number;
	pageContent: UploadedTempFilePage[];
	stageContent: IStageContentInfo[];
	/** Every piece of content the run has shown, removed or not: the protocol's phase rows. */
	stageContentHistory: IStageContentInfo[];
	setUploadedFiles: Dispatch<SetStateAction<UploadedTempFile[]>>;
	drawingCoords: { xCoords: number | null; yCoords: number | null; id?: string }[];
	sendDrawing: (
		coordsArray: { xCoords: number | null; yCoords: number | null; id: string }[]
	) => void;
	changeSelectedFileAndPages: (
		uploadedFileId?: number | null,
		uploadedFilePage?: number | null,
		blank?: boolean
	) => void;
	sendChatMessage: (message: string) => void;
	setPageContent: Dispatch<SetStateAction<UploadedTempFilePage[]>>;
	clearDrawing: (coordsArray: []) => void;
	sendProtocolMessage: (type: ScenarioMessageTypes, data: unknown) => void;
	updateProtocolMessage: (type: ScenarioMessageTypes, messageId: string, data: unknown) => void;
	updateProtocolTask: (messageId: string, change: ProtocolTaskChange) => void;
	sendScenarioAction: (type: ScenarioActionTypes, data: unknown) => void;
	getScenarioActualData: () => void;
	disconnectFromScenario: () => void;
	updateUserPermissions: (userId: string, updatedPermissions: PermissionRoles[]) => void;
	currentlySelectedFileAndPage?: SelectedFileAndPage;
	handleFileDownload: (uploadedFileId: number) => void;
	selectedDefaultParticipants?: IConnectedUser[];
	handleRaiseHand: (
		userId: number | string,
		courseProgressId: string,
		isHandRaised: boolean
	) => void;
}

/** Who is responsible for a protocol entry, and whether it is done (BCM360-376). */
export interface ProtocolTaskChange {
	assigneeId?: string | null;
	done?: boolean;
}

export enum SocketEvents {
	ACTION = "action",
	ACTUAL_SCENARIO_DATA = "actual_scenario_data",
	SCENARIO_ACTION = "scenario_action",
	GET_ACTUAL_SCENARIO_DATA = "get_actual_scenario_data",
	PROTOCOL = "protocol",
	CHAT = "chat",
	SEND_CHAT = "send_chat",
	UPDATE_PROTOCOL = "update_protocol",
	SEND_PROTOCOL = "send_protocol",
	UPDATE_CONNECTED_USERS = "update_connected_users",
	JOIN_ROOM = "join_room",
	ERROR = "error",
	HANDLE_RAISE_HAND = "handle_raise_hand",
	UPDATE_USER = "update_user",
	DRAW = "draw",
	CLEAR_DRAWING = "clear_drawing",
	UPDATE_USER_INFO = "update_user_info",
	UPDATE_PERMISSIONS = "update_permissions",
	SELECT_FILE_AND_PAGE = "select_file_and_page",
	DOWNLOAD_FILE = "download_file",
	UPDATE_PROTOCOL_TASK = "update_protocol_task",
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider = ({
	courseId,
	children,
	errInfo,
	currentAuth,
	onActionHandler,
}: {
	courseId: string;
	children: React.ReactNode;
	errInfo?: UserSchemaType;
	currentAuth: AuthState;
	onActionHandler: (data: any) => void;
}) => {
	const token = currentAuth?.token;
	const user = currentAuth?.user;
	const dispatch = useAppDispatch();

	const [socket, setSocket] = useState<Socket | null>(null);
	const [currentStage, setCurrentStage] = useState<number>(0);
	const [raisedHands, setRaisedHands] = useState<IRaisedHands[]>([]);
	const [currentStageEndTimestamp, setCurrentStageEndTimestamp] = useState<number>(0);
	const [connectedUsers, setConnectedUsers] = useState<IConnectedUser[]>([]);
	const [runUsers, setRunUsers] = useState<IConnectedUser[]>([]);
	const [selectedDefaultParticipants, setSelectedDefaultParticipants] = useState<IConnectedUser[]>(
		[]
	);
	const [chatMessages, setChatMessages] = useState<ScenarioMessageInfo[]>([]);
	const [protocolMessages, setProtocolMessages] = useState<ScenarioMessageInfo[]>([]);
	const [stageContent, setStageContent] = useState<IStageContentInfo[]>([]);
	const [stageContentHistory, setStageContentHistory] = useState<IStageContentInfo[]>([]);
	const [uploadedFiles, setUploadedFiles] = useState<UploadedTempFile[]>([]);
	const [currentlySelectedFileAndPage, setCurrentlySelectedFileAndPage] =
		useState<SelectedFileAndPage>();
	const [pageContent, setPageContent] = useState<UploadedTempFilePage[]>([]);
	const [drawingCoords, setDrawingCoords] = useState<
		{ xCoords: number | null; yCoords: number | null; id?: string; userId?: string | number }[]
	>([]);

	useEffect(() => {
		if (!token || !courseId) {
			return;
		}

		const newSocket = io(import.meta.env.VITE_API_URL, {
			query: { token },
			// The API runs several replicas/workers that don't share polling state, so the
			// multi-request polling handshake breaks. A websocket is one connection end to end.
			transports: ["websocket"],
			reconnection: true,
			reconnectionAttempts: Infinity,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 1000,
			timeout: 1000,
		});

		setSocket(newSocket);

		newSocket.emit(SocketEvents.JOIN_ROOM, courseId);

		newSocket.on(SocketEvents.CHAT, (data: any) => {
			setChatMessages((prevMessages) => {
				const messageExists = prevMessages.some((item) => item.id === data.id);

				if (messageExists) {
					return prevMessages.map((item) => (item.id === data.id ? data : item));
				} else {
					return [...prevMessages, data];
				}
			});
		});
		newSocket.on(SocketEvents.HANDLE_RAISE_HAND, (data: IRaisedHands[]) => {
			setRaisedHands(data);
		});

		newSocket.on(SocketEvents.SELECT_FILE_AND_PAGE, (data: ScenarioProgress) => {
			if (data.currentlySelectedFileId !== null && data.currentlySelectedPage !== null) {
				setCurrentlySelectedFileAndPage((prev) => ({
					...prev,
					id: data.currentlySelectedFileId!,
					page: data.currentlySelectedPage!,
					blank: data.blank ?? false,
				}));
			} else if (data.blank) {
				setCurrentlySelectedFileAndPage((prev) => ({
					...prev,
					id: data.currentlySelectedFileId!,
					page: data.currentlySelectedPage!,
					blank: data.blank ?? true,
				}));
			} else {
				setCurrentlySelectedFileAndPage((prev) => ({
					...prev,
					id: data.currentlySelectedFileId!,
					page: data.currentlySelectedPage!,
					blank: data.blank ?? false,
				}));
				setPageContent([]);
			}
		});

		newSocket.on(SocketEvents.DRAW, (data) => {
			if (data?.drawingCoords?.length > 0) {
				setDrawingCoords(data.drawingCoords);
			}
		});

		newSocket.on(SocketEvents.CLEAR_DRAWING, (data) => {
			setDrawingCoords([]);
		});

		newSocket.on(SocketEvents.DOWNLOAD_FILE, (data) => {
			if (!currentlySelectedFileAndPage) {
				return;
			}

			if (data?.uploadedFilePages) {
				setPageContent(data?.uploadedFilePages);
			}
		});

		newSocket.on(SocketEvents.PROTOCOL, (data: any) => {
			setProtocolMessages((prevMessages) => {
				const messageExists = prevMessages.some((item) => item.id === data.id);

				if (messageExists) {
					return prevMessages.map((item) => (item.id === data.id ? data : item));
				} else {
					return [...prevMessages, data];
				}
			});
		});

		newSocket.on(SocketEvents.ACTUAL_SCENARIO_DATA, (data: ScenarioProgress) => {
			setSelectedDefaultParticipants(data?.defaultParticipants);
			setProtocolMessages(data?.protocolHistory);
			setCurrentStage(data?.currentStage);
			setChatMessages(data?.chatHistory);
			setUploadedFiles(data?.uploadedFiles);
			setStageContentHistory(data?.stageContent ?? []);
			setStageContent(
				data.stageContent
					?.filter((item) => !item?.isRemoved)
					?.sort((a, b) => {
						const stageA = a?.stageNumber ?? Infinity;
						const stageB = b?.stageNumber ?? Infinity;

						if (stageA !== stageB) {
							return stageB - stageA;
						}

						const timeA = new Date(a?.timeStamp ?? 0).getTime();
						const timeB = new Date(b?.timeStamp ?? 0).getTime();

						return timeB - timeA;
					})
			);
			if (data?.currentStageEndTimestamp) {
				setCurrentStageEndTimestamp(data?.currentStageEndTimestamp);
			} else {
				setCurrentStageEndTimestamp(0);
			}

			if (data?.currentlySelectedFileId || data?.currentlySelectedPage || data?.blank) {
				setCurrentlySelectedFileAndPage({
					id: data?.currentlySelectedFileId ?? null,
					page: data?.currentlySelectedPage ?? null,
					blank: data?.blank ?? false,
				});
			}

			if (data?.drawingCoords && data?.drawingCoords?.length > 0) {
				setDrawingCoords(data?.drawingCoords);
			} else if (data?.drawingCoords?.length === 0) {
				setDrawingCoords([]);
			}
		});

		newSocket.on(SocketEvents.UPDATE_CONNECTED_USERS, (data: IConnectedUser[]) => {
			setConnectedUsers(data ? data.filter((user) => !user?.isRemoved) : []);
			setRunUsers(data ?? []);
		});

		newSocket.on(SocketEvents.UPDATE_PERMISSIONS, (data: any) => {
			dispatch(updatePermissions(data));
		});

		newSocket.on(SocketEvents.ACTION, (data: any) => {
			onActionHandler(data);
		});

		newSocket.on(SocketEvents.UPDATE_USER_INFO, (data: any) => {
			const merge = (prev: IConnectedUser[]) =>
				prev.map((user) => (user.id === data.id ? { ...user, ...data } : user));

			setConnectedUsers(merge);
			setRunUsers(merge);
		});

		return () => {
			newSocket.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		token,
		courseId,
		currentlySelectedFileAndPage?.id,
		setDrawingCoords,
		currentlySelectedFileAndPage?.blank,
	]);

	const sendChatMessage = useCallback(
		(message: string) => {
			if (socket) {
				socket.emit(SocketEvents.SEND_CHAT, { message, courseId });
			}
		},
		[socket, courseId]
	);

	const changeSelectedFileAndPages = useCallback(
		(uploadedFileId?: number | null, uploadedFilePage?: number | null, blank = false) => {
			if (socket) {
				socket.emit(SocketEvents.SELECT_FILE_AND_PAGE, {
					uploadedFileId,
					courseId,
					uploadedFilePage,
					blank,
				});
			}
		},
		[socket, courseId]
	);

	const handleFileDownload = useCallback(
		(uploadedFileId: number) => {
			if (socket) {
				socket.emit(SocketEvents.DOWNLOAD_FILE, {
					uploadedFileId,
					courseId,
				});
			}
		},
		[socket, courseId]
	);

	const sendProtocolMessage = useCallback(
		(type: ScenarioMessageTypes, data: unknown) => {
			if (socket) {
				socket.emit(SocketEvents.SEND_PROTOCOL, { type, data, courseId });
			}
		},
		[socket, courseId]
	);

	const updateProtocolMessage = useCallback(
		(type: ScenarioMessageTypes, messageId: string, data: unknown) => {
			if (socket) {
				socket.emit(SocketEvents.UPDATE_PROTOCOL, { type, data, messageId, courseId });
			}
		},
		[socket, courseId]
	);

	const updateProtocolTask = useCallback(
		(messageId: string, change: ProtocolTaskChange) => {
			if (socket) {
				socket.emit(SocketEvents.UPDATE_PROTOCOL_TASK, { ...change, messageId, courseId });
			}
		},
		[socket, courseId]
	);

	const getScenarioActualData = useCallback(() => {
		if (socket) {
			socket.emit(SocketEvents.GET_ACTUAL_SCENARIO_DATA, { courseId });
		}
	}, [socket, courseId]);

	const sendScenarioAction = useCallback(
		(type: ScenarioActionTypes, data: unknown) => {
			if (socket) {
				socket.emit(SocketEvents.SCENARIO_ACTION, { type, data, courseId });
			}
		},
		[socket, courseId]
	);

	const updateUserPermissions = useCallback(
		(userId: string, updatedPermissions: PermissionRoles[]) => {
			if (socket) {
				socket.emit(SocketEvents.UPDATE_USER, { userId, updatedPermissions, courseId });
			}
		},
		[socket, courseId]
	);

	const sendDrawing = useCallback(
		(coordsArray: { xCoords: number | null; yCoords: number | null; id: string }[]) => {
			if (socket) {
				socket.emit(SocketEvents.DRAW, { coordsArray, courseId });
			}
		},
		[socket, courseId]
	);

	const handleRaiseHand = useCallback(
		(userId: number | string, courseProgressId: string, isHandRaised: boolean) => {
			if (socket) {
				socket.emit(SocketEvents.HANDLE_RAISE_HAND, {
					userId,
					courseProgressId,
					isHandRaised,
				});
			}
		},
		[socket, courseId]
	);

	const clearDrawing = useCallback(
		(coordsArray: []) => {
			if (socket) {
				socket.emit(SocketEvents.CLEAR_DRAWING, { coordsArray, courseId });
			}
		},
		[socket, courseId]
	);

	const disconnectFromScenario = useCallback(() => {
		if (socket) {
			socket.disconnect();
		}
	}, [socket]);

	return (
		<SocketContext.Provider
			value={{
				socket,
				currentStage,
				handleRaiseHand,
				currentUserData: user,
				connectedUsers,
				runUsers,
				chatMessages,
				raisedHands,
				protocolMessages,
				currentStageEndTimestamp,
				stageContent,
				stageContentHistory,
				sendChatMessage,
				uploadedFiles,
				clearDrawing,
				changeSelectedFileAndPages,
				sendProtocolMessage,
				updateProtocolMessage,
				updateProtocolTask,
				sendScenarioAction,
				getScenarioActualData,
				disconnectFromScenario,
				updateUserPermissions,
				isCurrentUserERR: errInfo?.id === user?.id || errInfo?.email === user?.email,
				errInfo,
				sendDrawing,
				drawingCoords,
				setUploadedFiles,
				currentlySelectedFileAndPage,
				handleFileDownload,
				pageContent,
				setPageContent,
				selectedDefaultParticipants,
			}}
		>
			{children}
		</SocketContext.Provider>
	);
};

// Hook to use socket context
export const useSocket = () => {
	const context = useContext(SocketContext);

	if (!context) {
		throw new Error("useSocket must be used within a SocketProvider");
	}

	return context;
};
